import store from '@state/store'
import Vue from 'vue'
import VueRouter from 'vue-router'
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta'
// Adds a loading bar at the top during page loads.
import NProgress from 'nprogress/nprogress'
import getSignalRToken from '@services/signalr/getSignalRToken.js'
import { isCacheFresh } from '@/helpers/cache-helpers'
import { DurationUnits } from '@/constants/date/DurationUnits.js'
import routes from './routes'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'metaInfo',
})

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

const startRouteLoading = () => {
  // Begin loading animation. Only really required for initial page loads/refreshes.
  if (!store.getters.hasLoadedAppOnce) {
    store.dispatch('startLoadingApp')
  }

  // Only display the top loading bar after initial load
  if (store.getters.hasLoadedAppOnce) NProgress.start()
}
const stopRouteLoading = () => {
  // Complete the full page loading animation
  store.dispatch('setAppAsLoaded')
  store.dispatch('finishLoadingApp')
  NProgress.done()
}

/**
 *  Sets app in embedded state if embedded query is set in URL
 * @param {Object} routeTo
 */
const setEmbeddedMode = (routeTo) => {
  if (routeTo.query && isQueryEmbeddedMode(routeTo.query))
    store.dispatch('setEmbeddedState', true)
}

const isQueryEmbeddedMode = (query) => {
  return (
    Object.prototype.hasOwnProperty.call(query, 'embedded') ||
    (Object.prototype.hasOwnProperty.call(query, 'redirectFrom') &&
      query.redirectFrom.includes('embedded=true'))
  )
}

/**
 * Sets the context of the embed mode
 * @param {Object} routeTo
 */
const setEmbedContext = (routeTo) => {
  if (routeTo.query && doesQueryContainEmbedContext(routeTo.query))
    store.dispatch('setEmbedContext', routeTo.query.context)
}

const doesQueryContainEmbedContext = (query) => {
  return (
    Object.prototype.hasOwnProperty.call(query, 'context') ||
    (Object.prototype.hasOwnProperty.call(query, 'redirectFrom') &&
      query.redirectFrom.includes('context='))
  )
}

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  setEmbeddedMode(routeTo)
  setEmbedContext(routeTo)
  startRouteLoading()

  // Check if auth is required on this route
  // (including nested routes).
  const isPublic = routeTo.matched.some((route) => route.meta.public)

  // If auth isn't required for the route, just continue.
  if (isPublic) return next()

  // If auth is required and the user is logged in...
  if (store.getters['auth/isUserLoggedIn']) {
    // Validate the local access token...
    return store
      .dispatch('auth/refreshToken')
      .then(async (validUser) => {
        if (!validUser.isSuccess) throw Error('Failed to refresh token')
        // Attempt to retrieve SignalR tokens
        if (
          !store.getters['signalr/getAccessToken'] ||
          !store.getters['signalr/getWebSocketToken']
        )
          await getSignalRToken()

        if (!Vue.prototype.$signalRHub.isConnected())
          Vue.prototype.$signalRHub.connect()

        // Get user's profile if not already set or isn't fresh
        if (
          !store.getters['auth/currentUser'] ||
          !isCacheFresh({
            cacheDuration: 2,
            duractionUnits: DurationUnits.HOUR,
            lastUpdated: store.getters['auth/currentUser'].lastUpdated,
          })
        ) {
          const profileResult = await store.dispatch(
            'auth/getCurrentUserProfile'
          )
          if (!profileResult.isSuccess) return redirectToErrorPage()
          await store.dispatch('auth/getCurrentUserRoles')
        }

        await store.dispatch('users/fetchAllStaff')

        // Then continue if the token still represents a valid user,
        // otherwise redirect to login.
        stopRouteLoading()

        return validUser.isSuccess ? next() : redirectToLogin()
      })
      .catch(async (error) => {
        if (error.message === 'Failed to refresh token') {
          // Clear out store and redirect to login page on failure
          await store.dispatch('clearStore')
          return redirectToLogin()
        }

        return redirectToErrorPage()
      })
  }

  // If auth is required and the user is NOT currently logged in,
  // redirect to login.
  redirectToLogin()

  function redirectToLogin() {
    stopRouteLoading()
    // Pass the original route to the login component
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } })
  }

  function redirectToErrorPage(pageName = 'ErrorPage') {
    stopRouteLoading()
    // Maintains intended path in url bar but will redirect user to provided error page
    next({
      name: pageName,
      params: [routeTo.path],
      replace: true,
    })
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Complete the animation of the route progress bar.
  stopRouteLoading()
})

export default router
